import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { fetchOrgDeferrals } from "@/api/subscriptionSchedules";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import { useOrgDeferrals } from "@/hooks/useSubscriptionSchedules";
import type {
  OrgDeferralRow,
  OrgDeferralSortKey,
  OrgDeferralTotals,
} from "@/interfaces/subscriptionSchedules";
import { FY_START_LABEL, money, periodLabel, SectionHeading } from "./shared";

const SORT_KEYS: OrgDeferralSortKey[] = ["name", "opening", "added", "recognised", "closing"];
const asSortKey = (key: string): OrgDeferralSortKey =>
  SORT_KEYS.includes(key as OrgDeferralSortKey) ? (key as OrgDeferralSortKey) : "closing";

const amountCell = (v: string, bold = false) => (
  <Box
    sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: bold ? 700 : 400 }}
  >
    {money(v)}
  </Box>
);

const planLabel = (r: OrgDeferralRow) =>
  r.planName
    ? `${r.planName}${r.billingInterval ? ` · ${r.billingInterval === "year" ? "annual" : "monthly"}` : ""}`
    : "—";

/**
 * Organizations holding a deferred subscription balance in the fiscal year — server-paged, searched
 * and sorted. The line under it carries the totals for everything the search matches, which for an
 * empty search equal the roll-forward's.
 */
export default function OrgDeferralsBlotter({
  fy,
  onOpen,
}: {
  fy: number;
  onOpen: (organizationId: number) => void;
}) {
  const [totals, setTotals] = useState<OrgDeferralTotals | null>(null);

  const useData = (q: {
    search: string;
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<OrgDeferralRow> => {
    const { data, isLoading } = useOrgDeferrals({
      fy,
      search: q.search || undefined,
      sortBy: asSortKey(q.sortBy),
      sortDir: q.sortDir,
      page: q.page,
      limit: q.limit,
    });
    const pageTotals = data?.totals ?? null;
    useEffect(() => setTotals(pageTotals), [pageTotals]);
    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };

  const config: BlotterConfig<OrgDeferralRow> = {
    searchPlaceholder: "Search organizations",
    itemNoun: "organization",
    rowKey: (r) => r.organizationId,
    defaultSortKey: "closing",
    defaultSortDir: "desc",
    filterFields: [],
    useData,
    exportFilename: `deferred-subscriptions-fy${fy}-organizations.csv`,
    exportFetch: async (q) => {
      const res = await fetchOrgDeferrals({
        fy,
        search: q.search || undefined,
        page: q.page,
        limit: q.limit,
      });
      return { data: res.data, totalPages: res.totalPages };
    },
    emptyMessage: `No organizations hold a deferred subscription balance in FY ${fy}.`,
    columns: [
      {
        key: "name",
        label: "Organization",
        sortable: true,
        sortValue: (r) => r.organizationName,
        render: (r) => (
          <Box
            component="button"
            type="button"
            onClick={() => onOpen(r.organizationId)}
            sx={{
              appearance: "none",
              background: "none",
              border: "none",
              p: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13.5,
              fontWeight: 700,
              color: "var(--color-rodeo-black)",
              textDecoration: "underline",
              textUnderlineOffset: 3,
              textAlign: "left",
            }}
          >
            {r.organizationName}
          </Box>
        ),
      },
      { key: "plan", label: "Plan", sortValue: planLabel, render: planLabel },
      {
        key: "cycles",
        label: "Billing periods",
        headerStyle: { textAlign: "right" },
        sortValue: (r) => r.cycles,
        render: (r) => <Box sx={{ textAlign: "right" }}>{r.cycles}</Box>,
        exportValue: (r) => String(r.cycles),
      },
      {
        key: "opening",
        label: `Owed at ${FY_START_LABEL}`,
        headerStyle: { textAlign: "right" },
        sortable: true,
        sortValue: (r) => Number(r.opening),
        render: (r) => amountCell(r.opening),
        exportValue: (r) => r.opening,
      },
      {
        key: "added",
        label: "Paid this year",
        headerStyle: { textAlign: "right" },
        sortable: true,
        sortValue: (r) => Number(r.added),
        render: (r) => amountCell(r.added),
        exportValue: (r) => r.added,
      },
      {
        key: "recognised",
        label: "Delivered this year",
        headerStyle: { textAlign: "right" },
        sortable: true,
        sortValue: (r) => Number(r.recognised),
        render: (r) => amountCell(r.recognised),
        exportValue: (r) => r.recognised,
      },
      {
        key: "closing",
        label: "Still owed",
        headerStyle: { textAlign: "right" },
        sortable: true,
        sortValue: (r) => Number(r.closing),
        render: (r) => amountCell(r.closing, true),
        exportValue: (r) => r.closing,
      },
      {
        key: "next",
        label: "Next release",
        sortValue: (r) => r.nextPeriod ?? "",
        render: (r) =>
          r.nextPeriod ? (
            `${periodLabel(r.nextPeriod)} · ${money(r.nextAmount)}`
          ) : (
            <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>Completed</Typography>
          ),
        exportValue: (r) => (r.nextPeriod ? `${r.nextPeriod} ${r.nextAmount}` : ""),
      },
    ],
  };

  return (
    <Box component="section" aria-label="Organizations">
      <SectionHeading
        title={
          <>
            Organizations{" "}
            {totals && (
              <Typography component="span" sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                · {totals.organizations.toLocaleString("en-US")} with a deferred balance in FY {fy}
              </Typography>
            )}
          </>
        }
      />
      <Blotter key={fy} config={config} />
      {totals && totals.organizations > 0 && (
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            fontSize: 13,
            color: "var(--color-graphite)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <span>
            All {totals.organizations.toLocaleString("en-US")} matching ·{" "}
            {totals.cycles.toLocaleString("en-US")} billing periods
          </span>
          <span>
            Owed at {FY_START_LABEL} {money(totals.opening)}
          </span>
          <span>Paid {money(totals.added)}</span>
          <span>Delivered {money(totals.recognised)}</span>
          <strong style={{ color: "var(--color-rodeo-black)" }}>
            Still owed {money(totals.closing)}
          </strong>
        </Box>
      )}
    </Box>
  );
}
